import { Link } from "react-router-dom";
import s from "./platformAdmin.module.css";

export function formatRub(n) {
  return new Intl.NumberFormat("ru-RU").format(Number(n) || 0);
}

export function formatWeekLabel(isoWeek) {
  if (!isoWeek) return "";
  const d = new Date(isoWeek);
  return d.toLocaleDateString("ru-RU", { day: "numeric", month: "short" });
}

export function MetricCard({ label, value, hint, trend, accent }) {
  return (
    <article className={`${s.metricCard} ${accent ? s.metricCardAccent : ""}`}>
      <div className={s.metricLabel}>{label}</div>
      <div className={s.metricValue}>{value}</div>
      {hint ? <div className={s.metricHint}>{hint}</div> : null}
      {trend ? (
        <div
          className={`${s.metricTrend} ${
            trend === "up"
              ? s.metricTrendUp
              : trend === "down"
                ? s.metricTrendDown
                : ""
          }`}
        >
          {trend === "up" ? "↑ рост" : trend === "down" ? "↓ снижение" : "→ стабильно"}
        </div>
      ) : null}
    </article>
  );
}

export function BarChart({ title, series = [], valueSuffix = "" }) {
  const max = Math.max(...series.map((p) => p.value), 1);
  return (
    <div className={s.chartPanel}>
      {title ? <h3 className={s.panelTitle}>{title}</h3> : null}
      <div className={s.barChart}>
        {series.map((point) => (
          <div key={point.week} className={s.barCol}>
            <div className={s.barTrack}>
              <div
                className={s.barFill}
                style={{ height: `${Math.max(6, (point.value / max) * 100)}%` }}
                title={`${point.value}${valueSuffix}`}
              />
            </div>
            <span className={s.barLabel}>{formatWeekLabel(point.week)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const INSIGHT_CLASS = {
  positive: "insightPositive",
  warning: "insightWarning",
  neutral: "insightNeutral",
};

export function InsightList({ items = [] }) {
  if (!items.length) return null;
  return (
    <div className={s.insightList}>
      {items.map((item, i) => (
        <div
          key={`${item.type}-${i}`}
          className={`${s.insightItem} ${s[INSIGHT_CLASS[item.type] || "insightNeutral"]}`}
        >
          {item.text}
        </div>
      ))}
    </div>
  );
}

export function HealthRing({ score = 0 }) {
  const pct = Math.min(100, Math.max(0, score));
  return (
    <div className={s.healthRing} style={{ "--pct": pct }}>
      <div className={s.healthRingInner}>
        <strong>{pct}</strong>
        <span>health</span>
      </div>
    </div>
  );
}

export function TopClubsTable({ title, rows = [], valueKey, valueLabel }) {
  if (!rows.length) return null;
  return (
    <div className={s.chartPanel}>
      <h3 className={s.panelTitle}>{title}</h3>
      <div className={s.topList}>
        {rows.map((row, idx) => (
          <div key={row.id} className={s.topRow}>
            <span className={s.topRank}>{idx + 1}</span>
            <div className={s.topMeta}>
              <strong>{row.name}</strong>
              <small>{row.subdomain}</small>
            </div>
            <span className={s.topValue}>
              {valueKey === "revenue_30d"
                ? `${formatRub(row[valueKey])} ₽`
                : row[valueKey]}
              {valueLabel ? ` ${valueLabel}` : ""}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const PLAN_CARD_CLASS = {
  demo: "planCardDemo",
  basic: "planCardBasic",
  pro: "planCardPro",
  business: "planCardBusiness",
  other: "planCardOther",
};

export function PlanBucketCard({ bucket, label, count, mrrRub }) {
  const cardClass = PLAN_CARD_CLASS[bucket] || PLAN_CARD_CLASS.other;
  return (
    <article className={`${s.planCard} ${s[cardClass]}`}>
      <div className={s.planCardLabel}>{label}</div>
      <div className={s.planCardValue}>{count}</div>
      {mrrRub > 0 ? (
        <div className={s.planCardHint}>MRR {formatRub(mrrRub)} ₽</div>
      ) : (
        <div className={s.planCardHint}>клубов</div>
      )}
    </article>
  );
}

function formatDateRu(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso}T12:00:00`);
  return d.toLocaleDateString("ru-RU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function daysLeftClass(days) {
  if (days == null) return "";
  if (days < 0) return s.daysExpired;
  if (days <= 3) return s.daysUrgent;
  if (days <= 14) return s.daysWarning;
  return "";
}

function planBadgeClass(bucket) {
  const map = {
    basic: s.planBadgeBasic,
    pro: s.planBadgePro,
    business: s.planBadgeBusiness,
    other: s.planBadgeOther,
  };
  return map[bucket] || s.planBadgeOther;
}

function statusPillClass(row) {
  if (row.status === "suspended") return s.statusSuspended;
  if (row.is_demo_expired) return s.statusExpired;
  if (row.is_trial) return s.statusTrial;
  if (row.is_paid) return s.statusActive;
  return s.statusNeutral;
}

export function SubscriptionsTable({
  title,
  rows = [],
  emptyText = "Нет записей",
  showClients = true,
}) {
  return (
    <div className={s.chartPanel}>
      {title ? <h3 className={s.panelTitle}>{title}</h3> : null}
      {!rows.length ? (
        <p className={s.emptyHint}>{emptyText}</p>
      ) : (
        <>
          <div className={s.mobileCardList}>
            {rows.map((row) => (
              <Link
                key={row.id}
                to={`/admin/tenants/${row.id}`}
                className={s.mobileDataCard}
              >
                <div className={s.mobileDataCardTop}>
                  <strong>{row.name}</strong>
                  <span
                    className={`${s.statusPill} ${statusPillClass(row)}`}
                  >
                    {row.is_demo_expired ? "Демо истекло" : row.status_label}
                  </span>
                </div>
                <small className={s.mobileDataCardSub}>{row.subdomain}</small>
                <div className={s.mobileDataCardMeta}>
                  <span>{row.plan_name}</span>
                  <span
                    className={`${s.daysLeft} ${daysLeftClass(row.days_left)}`}
                  >
                    {row.days_left == null
                      ? "—"
                      : row.days_left < 0
                        ? `${Math.abs(row.days_left)} дн. назад`
                        : `${row.days_left} дн.`}
                  </span>
                </div>
                <div className={s.mobileDataCardMeta}>
                  <span>до {formatDateRu(row.paid_until)}</span>
                  <span>
                    {row.monthly_price_rub
                      ? `${formatRub(row.monthly_price_rub)} ₽/мес`
                      : "—"}
                  </span>
                </div>
                {showClients ? (
                  <div className={s.mobileDataCardMeta}>
                    <span>Спортсмены: {row.clients ?? 0}</span>
                  </div>
                ) : null}
              </Link>
            ))}
          </div>

          <div className={`${s.subTableWrap} ${s.desktopOnlyBlock}`}>
            <table className={s.subTable}>
              <thead>
                <tr>
                  <th>Клуб</th>
                  <th>Тариф</th>
                  <th>Статус</th>
                  <th>Оплачено до</th>
                  <th>Осталось</th>
                  <th>Цена</th>
                  {showClients ? <th>Спортсмены</th> : null}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id}>
                    <td>
                      <Link
                        to={`/admin/tenants/${row.id}`}
                        className={s.subClubLink}
                      >
                        <strong>{row.name}</strong>
                        <small>{row.subdomain}</small>
                      </Link>
                    </td>
                    <td>
                      <span
                        className={`${s.planBadge} ${planBadgeClass(row.plan_bucket)}`}
                      >
                        {row.plan_name}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`${s.statusPill} ${statusPillClass(row)}`}
                      >
                        {row.is_demo_expired
                          ? "Демо истекло"
                          : row.status_label}
                      </span>
                    </td>
                    <td>{formatDateRu(row.paid_until)}</td>
                    <td>
                      <span
                        className={`${s.daysLeft} ${daysLeftClass(row.days_left)}`}
                      >
                        {row.days_left == null
                          ? "—"
                          : row.days_left < 0
                            ? `${Math.abs(row.days_left)} дн. назад`
                            : `${row.days_left} дн.`}
                      </span>
                    </td>
                    <td>
                      {row.monthly_price_rub
                        ? `${formatRub(row.monthly_price_rub)} ₽/мес`
                        : "—"}
                    </td>
                    {showClients ? <td>{row.clients ?? 0}</td> : null}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
